// GenerationProgress.tsx
import React from "react";
import { Sparkles, Loader } from "lucide-react";

export type PlanNode = {
    title?: string;
    name?: string;
    index: number;
    has_children?: boolean;
    inputs?: { children?: PlanNode[] };
};

function flattenPlanNodes(nodes?: PlanNode[], arr: PlanNode[] = []): PlanNode[] {
    if (!nodes) return arr;
    for (const n of nodes) {
        arr.push(n);
        if (n.has_children && n.inputs?.children) {
            flattenPlanNodes(n.inputs.children, arr);
        }
    }
    return arr;
}

type GenerationProgressProps = {
    state: "idle" | "waitingForPlan" | "executing";
    currentStep: number;
    totalSteps: number;
    currentTask: string;
    plan?: { plan?: PlanNode[] } | null;
};

export default function GenerationProgress({
    state,
    currentStep,
    totalSteps,
    currentTask,
    plan,
}: GenerationProgressProps) {

    // Avancement en %
    const pct =
        totalSteps > 0
            ? Math.min((currentStep / totalSteps) * 100, 100)
            : 0;

    // Extraire toutes les noeuds du plan (à plat)
    const nodes = plan?.plan ? flattenPlanNodes(plan.plan) : [];

    // Trouver l'index du noeud courant
    const currentNode =
        nodes.find((n) => n.title === currentTask || n.name === currentTask) ||
        nodes[currentStep] ||
        null;

    // Prochaine tâche (future node)
    const nextNode =
        nodes[currentStep + 1] || null;

    const finished = state === "idle" && pct === 100;

    return (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-white shadow-lg rounded-2xl p-6 w-96 flex flex-col gap-4">
                <h2 className="text-lg font-semibold text-center text-gray-900 flex items-center gap-2 justify-center">
                    {finished ? (
                        <span className="flex items-center gap-2 text-green-600"><Sparkles className="w-5 h-5" /> Génération terminée !</span>
                    ) : state === "waitingForPlan" ? (
                        <span className="flex items-center gap-2 text-brixel"><Loader className="w-5 h-5 animate-spin" /> Préparation du plan…</span>
                    ) : (
                        <span className="flex items-center gap-2 text-brixel"><Sparkles className="w-5 h-5" /> Génération en cours…</span>
                    )}
                </h2>
                <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden" title={`Étape ${currentStep + 1} sur ${totalSteps}`}>
                    <div
                        className="bg-brixel h-full transition-all duration-300"
                        style={{ width: `${pct}%` }}
                    />
                </div>
                <div className="flex gap-1 items-baseline justify-center text-xs">
                    <span className="text-gray-500">{currentStep + 1}</span>
                    <span>/</span>
                    <span className="text-gray-500">{totalSteps}</span>
                    <span className="ml-2 text-gray-700">
                        {currentNode ? (currentNode.title || currentNode.name) : currentTask}
                    </span>
                </div>
                {/* NOUVEAU : Affichage étapes à venir */}
                {!!nextNode && (
                    <div className="bg-gray-100 rounded-lg px-3 py-2 text-xs text-gray-600">
                        <strong className="text-gray-700">Étape suivante :</strong> {nextNode.title || nextNode.name}
                    </div>
                )}
                {/* TODO : On peut montrer + d'étapes suivantes ici */}
                {nodes.length > 0 && (
                    <div className="bg-gray-50 rounded-md px-2 py-1 text-xs text-gray-500">
                        {
                            nodes.slice(currentStep + 1, currentStep + 4).map((n, i) => (
                                <div key={n.index} className="flex items-center gap-1">
                                    <span>•</span>
                                    <span>{n.title || n.name}</span>
                                </div>
                            ))
                        }
                        {nodes.length > currentStep + 4 && (
                            <span className="text-gray-400">…</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
